import { useAtomValue } from 'jotai';
import { lazy, Suspense } from 'react';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { OktaAuth } from '@okta/okta-auth-js'; // Correct import
import { LoginCallback, Security } from '@okta/okta-react';

import {
  ChatConnectionProvider,
  ConnectionProvider,
  FeatureFlag,
  FeatureFlagProvider,
  SocketProvider,
} from '@frontend/contexts';

import CanInstance from './components/CanInstance';
import CanUser from './components/CanUser';
import CapabilityOrFlagGuard from './components/CapabilityOrFlagGuard';
import { ErrorBoundary } from './components/error-boundary';
import FullScreenLoader from './components/FullScreenLoader';
import { OptiGlobalHeader } from './components/optiGlobalHeader';
import { OpalOverlayButton } from './components/optiGlobalHeader/components/OpalOverlayButton';
import PathNavigator from './components/PathNavigator';
import RequireAuth from './components/RequireAuth'; // Custom auth wrapper for protected pages
import RequireAuthOnly from './components/RequireAuthOnly';
import { BaseLayout } from './layouts/baseLayout';
import { isDesktopApp } from './utils/desktop';
import { SentryErrorBoundary } from './utils/sentry';
import { globalOptiHeaderVisibleAtom } from './atoms';
import { FeatureFlags, UxFeaturesVariables } from './constants';
import { useFontSize, useInstanceId, useTheme } from './hooks';
import oktaAuth from './oktaAuth';

const AgentPage = lazy(() =>
  import(/* webpackChunkName: "AgentPage" */ './features').then(m => ({ default: m.AgentPage }))
);
const Chat = lazy(() =>
  import(/* webpackChunkName: "Chat" */ './features/chat').then(m => ({ default: m.Chat }))
);
const InstructionPage = lazy(() =>
  import(/* webpackChunkName: "InstructionPage" */ './features/userInstructions/components').then(
    m => ({ default: m.InstructionPage })
  )
);
const ContextPage = lazy(() =>
  import(/* webpackChunkName: "ContextPage" */ './features/context').then(m => ({
    default: m.ContextPage,
  }))
);
const RAGConfiguration = lazy(() =>
  import(/* webpackChunkName: "RAGConfiguration" */ './features/rag').then(m => ({
    default: m.RAGConfiguration,
  }))
);
const SettingsPage = lazy(
  () => import(/* webpackChunkName: "SettingsPage" */ './features/settings/Settings')
);
const NotificationCenterPage = lazy(() =>
  import(/* webpackChunkName: "NotificationCenterPage" */ './features/notifications').then(m => ({
    default: m.NotificationCenterPage,
  }))
);
const ArtifactsPage = lazy(() =>
  import(/* webpackChunkName: "ArtifactsPage" */ './features/artifacts').then(m => ({
    default: m.ArtifactsPage,
  }))
);
const ToolsManagement = lazy(() =>
  import(/* webpackChunkName: "ToolsManagement" */ './features').then(m => ({
    default: m.ToolsManagement,
  }))
);
const WorkflowExecutionPage = lazy(() =>
  import(
    /* webpackChunkName: "WorkflowExecutionPage" */ './features/agents/components/workflow-execution'
  ).then(m => ({
    default: m.WorkflowExecutionPage,
  }))
);
const ToolsWarningBanner = lazy(() =>
  import(
    /* webpackChunkName: "ToolsWarningBanner" */ './features/tools-management/components/ToolsWarningBanner/ToolsWarningBanner'
  ).then(m => ({
    default: m.ToolsWarningBanner,
  }))
);
const RedirectHandler = lazy(
  () => import(/* webpackChunkName: "RedirectHandler" */ './components/RedirectHandler')
);
const InstancePicker = lazy(() =>
  import(/* webpackChunkName: "InstancePicker" */ './features/instancePicker').then(m => ({
    default: m.InstancePicker,
  }))
);

function App() {
  return (
    <SentryErrorBoundary fallback={<p>An error has occurred</p>}>
      <WrappedSecurity />
    </SentryErrorBoundary>
  );
}

function WrappedSecurity() {
  const navigate = useNavigate();
  const location = useLocation();
  const { instanceId } = useInstanceId();
  const globalOptiHeaderVisible = useAtomValue(globalOptiHeaderVisibleAtom);
  useTheme();
  useFontSize();

  const segments = location.pathname.split('/');
  const isOnOverlayPage = ['instructions', 'agents', 'tools', 'context'].some(s =>
    segments.includes(s)
  );
  const restoreOriginalUri = async (_oktaAuth: OktaAuth, originalUri?: string): Promise<void> => {
    navigate(originalUri || '/');
  };

  return (
    <Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri}>
      <Routes>
        <Route path="/login" element={<LoginCallback />} />
        <Route
          path="/instances"
          element={
            <RequireAuthOnly>
              <Suspense fallback={<FullScreenLoader />}>
                <InstancePicker />
              </Suspense>
            </RequireAuthOnly>
          }
        />
        <Route
          path="/redirect"
          element={
            <RequireAuthOnly>
              <Suspense fallback={<FullScreenLoader />}>
                <RedirectHandler />
              </Suspense>
            </RequireAuthOnly>
          }
        />
        <Route
          path="/instances/:instanceId"
          element={
            <RequireAuth>
              <FeatureFlagProvider>
                <ErrorBoundary>
                  <OptiGlobalHeader visible={globalOptiHeaderVisible && !isDesktopApp()}>
                    {isOnOverlayPage && <OpalOverlayButton />}
                  </OptiGlobalHeader>
                </ErrorBoundary>
                <SocketProvider>
                  <Suspense fallback={null}>
                    <ToolsWarningBanner />
                  </Suspense>
                  <BaseLayout />
                </SocketProvider>
              </FeatureFlagProvider>
            </RequireAuth>
          }
        >
          <Route
            path="chat/*"
            element={
              <CanUser operations={['use_chat']}>
                <ChatConnectionProvider>
                  <Suspense fallback={<FullScreenLoader />}>
                    <Chat />
                  </Suspense>
                </ChatConnectionProvider>
              </CanUser>
            }
          />
          <Route
            path="instructions/*"
            element={
              <CanUser operations={['view_instructions', 'manage_instructions', 'use_chat']}>
                <ConnectionProvider>
                  <Suspense fallback={<FullScreenLoader />}>
                    <InstructionPage />
                  </Suspense>
                </ConnectionProvider>
              </CanUser>
            }
          />
          <Route
            path="context/*"
            element={
              <CanUser
                operations={[
                  'view_instructions',
                  'manage_instructions',
                  'use_chat',
                  'manage_rag_config',
                ]}
              >
                <FeatureFlag
                  featureKey={FeatureFlags.UX_FEATURES}
                  variable={UxFeaturesVariables.REORG_RAG_CONTEXT}
                >
                  <Suspense fallback={<FullScreenLoader />}>
                    <ContextPage />
                  </Suspense>
                </FeatureFlag>
              </CanUser>
            }
          />
          <Route
            path="artifacts/*"
            element={
              <CanUser operations={['use_chat']}>
                <FeatureFlag featureKey="opal_artifacts">
                  <Suspense fallback={<FullScreenLoader />}>
                    <ArtifactsPage />
                  </Suspense>
                </FeatureFlag>
              </CanUser>
            }
          />
          <Route
            path="tools/*"
            element={
              <CanUser operations={['view_tools', 'use_chat']}>
                <Suspense fallback={<FullScreenLoader />}>
                  <ToolsManagement />
                </Suspense>
              </CanUser>
            }
          />
          <Route
            path="settings/*"
            element={
              <CanUser operations={['use_chat']}>
                <Suspense fallback={<FullScreenLoader />}>
                  <SettingsPage />
                </Suspense>
              </CanUser>
            }
          />
          <Route
            path="notifications/*"
            element={
              <FeatureFlag featureKey="notifications">
                <Suspense fallback={<FullScreenLoader />}>
                  <NotificationCenterPage />
                </Suspense>
              </FeatureFlag>
            }
          />
          <Route
            path="agents/workflow/executions/:executionId/*"
            element={
              <CapabilityOrFlagGuard
                capabilities={['workflows_full_enabled', 'workflows_chat_enabled']}
                featureFlags={['workflow-agent']}
              >
                <CanUser operations={['view_agents']}>
                  <Suspense fallback={<FullScreenLoader />}>
                    <WorkflowExecutionPage />
                  </Suspense>
                </CanUser>
              </CapabilityOrFlagGuard>
            }
          />
          <Route
            path="agents/*"
            element={
              <CanUser operations={['view_agents']}>
                <Suspense fallback={<FullScreenLoader />}>
                  <AgentPage />
                </Suspense>
              </CanUser>
            }
          />
          <Route
            path="rag/*"
            element={
              <CanUser operations={['manage_rag_config']}>
                <CanInstance capabilities={['RAG_enabled']}>
                  <FeatureFlag featureKey="shamwow-settings-page">
                    <Suspense fallback={<FullScreenLoader />}>
                      <RAGConfiguration />
                    </Suspense>
                  </FeatureFlag>
                </CanInstance>
              </CanUser>
            }
          />
          <Route path="" element={<PathNavigator path={`/instances/${instanceId}/chat`} />} />
        </Route>
        <Route
          path="*"
          element={
            !instanceId ? (
              <RequireAuthOnly>
                <PathNavigator path="/instances" />
              </RequireAuthOnly>
            ) : (
              <RequireAuth>
                <PathNavigator path={`/instances/${instanceId}/chat`} />
              </RequireAuth>
            )
          }
        />
      </Routes>
    </Security>
  );
}

export default App;
